<template>
	<div>
		<v-data-table
			v-if="roleName !== 'ORGANIZATION_ADMIN'"
			:loading="loading"
			:headers="headers"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:search="appBarSearch"
			:items="filteredUsers"
			sort-by="id"
			item-key="id"
			fixed-header
			dense
			:height="pageHeight(174)"
			:footer-props="getDataTableFooterProps('medicalStaffTable')"
			:items-per-page="-1"
			class="data-table-outline"
			style="border-radius: 0">
			<template #item="{ item, index }">
				<v-tooltip top v-if="item.status === 10">
					<template #activator="{ on, attrs }">
						<tr v-bind="attrs" v-on="on" :class="[getRowsForDataTable(index), 'disabled-row']">
							<td class="table-font-size px-0 mx-0">
								<v-icon class="ml-2" small :style="getStatusColor(item)">mdi-checkbox-blank-circle</v-icon>
								<span class="ml-1">{{ item.fullName }}</span>
							</td>
							<td class="table-font-size">
								<div class="table-cols d-flex">
									<v-img max-width="15" :src="getIconForRole(item.roleData)"></v-img>
									<span class="ml-2">
										{{
											$i18n?.locale === 'en' ? item.roleData.roleNameForDisplay : item.roleData.roleNameForDisplayESP
										}}
									</span>
								</div>
							</td>
							<td class="table-font-size">
								<v-icon size="15" color="primary">mdi-email</v-icon>
								<span>{{ item.email }}</span>
							</td>
							<td class="table-font-size">
								<span>{{ item.phone }}</span>
							</td>
							<td></td>
							<td></td>
						</tr>
					</template>
					{{ $t('staff-is-controlled-by-ecp') }}
				</v-tooltip>
				<tr v-else :class="[getRowsForDataTable(index), '']" @click="editDrawerDetails(item)">
					<td class="table-font-size px-0 mx-0">
						<v-icon class="ml-2" small :style="getStatusColor(item)">mdi-checkbox-blank-circle</v-icon>
						<span class="ml-1">{{ item.fullName }}</span>
					</td>
					<td class="table-font-size">
						<div class="table-cols d-flex">
							<v-img max-width="15" :src="getIconForRole(item.roleData)"></v-img>
							<span class="ml-2">
								{{ $i18n?.locale === 'en' ? item.roleData.roleNameForDisplay : item.roleData.roleNameForDisplayESP }}
							</span>
						</div>
					</td>
					<td class="table-font-size">
						<v-icon size="15" color="primary">mdi-email</v-icon>
						<span>{{ item.email }}</span>
					</td>
					<td class="table-font-size">
						<span>{{ item.phone }}</span>
					</td>
					<td class="text-center">
						<v-switch
							density="compact"
							readonly
							@click.stop="openConfirmDialog(item)"
							v-model="item.status"
							:true-value="1"
							:false-value="-1"
							class="mt-0 pt-0"
							hide-details />
					</td>
					<td>
						<div class="d-flex align-center justify-center">
							<v-menu left bottom class="py-0" offset-y transition="slide-x-transition">
								<template #activator="{ on, attrs }">
									<v-icon color="black" dense v-bind="attrs" v-on="on">mdi-dots-horizontal</v-icon>
								</template>
								<v-list class="px-0" dense>
									<v-list-item class="py-0 my-0" link dense @click="editDrawerDetails(item)">
										<v-list-item-icon class="mx-1">
											<v-icon class="icon-img mdi-18px pt-1" color="black">mdi-eye</v-icon>
										</v-list-item-icon>
										<v-list-item-content class="font-bold text-body-2">{{ $t('view') }}</v-list-item-content>
									</v-list-item>
									<v-list-item link dense @click="handleActionSendEmail(item)">
										<v-list-item-icon class="mx-1">
											<v-icon class="icon-img mdi-18px" color="black">mdi-lock-reset</v-icon>
										</v-list-item-icon>
										<v-list-item-content
											class="font-bold font-size14"
											v-if="item.status !== 1"
											label
											@click.stop="sendActivationEmail(item, 'USER_CONFIRMATION')"
											>{{ $t('send-activation-email') }}</v-list-item-content
										>
										<v-list-item-content
											class="font-bold font-size14 text-left"
											v-else
											label
											@click.stop="sendActivationEmail(item, 'RESET_PASSWORD')"
											>{{ $t('send-reset-password-email') }}</v-list-item-content
										>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</td>
				</tr>
			</template>
			<template #[`footer.prepend`]>
				<span class="font-weight-bold mx-3">{{ $t('table-legend') }}</span>
				<v-tooltip color="green" top>
					<template #activator="{ on }">
						<div class="d-flex">
							<span class="font-weight-medium font-size12-5">{{ $t('active') }}</span>
							<v-icon class="mdi-18px color-green px-2" v-on="on">mdi-checkbox-blank-circle</v-icon>
						</div>
					</template>
					<span>{{ $t('active') }}</span>
				</v-tooltip>
				<v-tooltip color="grey-darken-2" top>
					<template #activator="{ on }">
						<div class="d-flex">
							<span class="font-weight-medium font-size12-5">{{ $t('inactive') }}</span>
							<v-icon class="mdi-18px px-2" v-on="on">mdi-checkbox-blank-circle</v-icon>
						</div>
					</template>
					<span>{{ $t('inactive') }}</span>
				</v-tooltip>
			</template>
		</v-data-table>
		<v-data-table
			v-else
			:loading="loading"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:items="searchUsers"
			:headers-length="2"
			sort-by="id"
			item-key="id"
			fixed-header
			single-expand
			dense
			:height="pageHeight(174)"
			:footer-props="getDataTableFooterProps('medicalStaffTable')"
			:items-per-page="-1">
			<template #item="{ item, index, expand, isExpanded }">
				<tr :class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]" @click="expand(!isExpanded)">
					<td class="text-left" style="width: 600px">
						<v-btn small icon>
							<v-icon small color="primary">
								{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
							</v-icon>
						</v-btn>
						<span>{{ item.hospitalName }}</span>
					</td>
					<td class="text-left">
						<v-chip class="white--text" pill x-small color="primary">
							{{ item.users.length }}
						</v-chip>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3" colspan="2">
						<v-data-table
							:items="item.users"
							:headers="headers"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							:height="pageHeight(95)"
							fixed-header>
							<template #item="{ item, index }">
								<v-tooltip top v-if="item.status === 10">
									<template #activator="{ on, attrs }">
										<tr v-bind="attrs" v-on="on" :class="[getRowsForDataTable(index), 'disabled-row']">
											<td class="table-font-size px-0 mx-0">
												<v-icon class="ml-2" small :style="getStatusColor(item)">mdi-checkbox-blank-circle</v-icon>
												<span class="ml-1">{{ item.fullName }}</span>
											</td>
											<td class="table-font-size">
												<div class="table-cols d-flex">
													<v-img max-width="15" :src="getIconForRole(item.roleData)"></v-img>
													<span class="ml-2">
														{{
															$i18n?.locale === 'en'
																? item.roleData.roleNameForDisplay
																: item.roleData.roleNameForDisplayESP
														}}
													</span>
												</div>
											</td>
											<td class="table-font-size">
												<v-icon size="15" color="primary">mdi-email</v-icon>
												<span>{{ item.email }}</span>
											</td>
											<td class="table-font-size">
												<span>{{ item.phone }}</span>
											</td>
											<td></td>
											<td></td>
										</tr>
									</template>
									{{ $t('staff-is-controlled-by-ecp') }}
								</v-tooltip>
								<tr v-else :class="getRowsForDataTable(index)" @click="editDrawerDetails(item)">
									<td class="table-font-size px-0 mx-0">
										<v-icon class="ml-2" small :style="getStatusColor(item)">mdi-checkbox-blank-circle</v-icon>
										<span class="ml-1">{{ item.fullName }}</span>
									</td>
									<td class="table-font-size">
										<div class="table-cols d-flex">
											<v-img max-width="15" :src="getIconForRole(item.roleData)"></v-img>
											<span class="ml-2">
												{{
													$i18n?.locale === 'en'
														? item.roleData.roleNameForDisplay
														: item.roleData.roleNameForDisplayESP
												}}
											</span>
										</div>
									</td>
									<td class="table-font-size">
										<v-icon size="15" color="primary">mdi-email</v-icon>
										<span>{{ item.email }}</span>
									</td>
									<td class="table-font-size">
										<span>{{ item.phone }}</span>
									</td>
									<td class="text-center">
										<v-switch
											density="compact"
											readonly
											@click.stop="openConfirmDialog(item)"
											v-model="item.status"
											:true-value="1"
											:false-value="-1"
											class="mt-0 pt-0"
											hide-details />
									</td>
									<td>
										<div class="d-flex align-center justify-center">
											<v-menu left bottom class="py-0" offset-y transition="slide-x-transition">
												<template #activator="{ on, attrs }">
													<v-icon color="black" dense v-bind="attrs" v-on="on">mdi-dots-horizontal</v-icon>
												</template>
												<v-list class="px-0" dense>
													<v-list-item class="py-0 my-0" link dense @click="editDrawerDetails(item)">
														<v-list-item-icon class="mx-1">
															<v-icon class="icon-img mdi-18px pt-1" color="black">mdi-eye</v-icon>
														</v-list-item-icon>
														<v-list-item-content class="font-bold text-body-2">{{ $t('view') }}</v-list-item-content>
													</v-list-item>
													<v-list-item link dense @click="handleActionSendEmail(item)">
														<v-list-item-icon class="mx-1">
															<v-icon class="icon-img mdi-18px" color="black">mdi-lock-reset</v-icon>
														</v-list-item-icon>
														<v-list-item-content
															class="font-bold font-size14"
															v-if="item.status !== 1"
															label
															@click.stop="sendActivationEmail(item, 'USER_CONFIRMATION')">
															{{ $t('send-activation-email') }}
														</v-list-item-content>
														<v-list-item-content
															class="font-bold font-size14 text-left"
															v-else
															label
															@click.stop="sendActivationEmail(item, 'RESET_PASSWORD')">
															{{ $t('send-reset-password-email') }}
														</v-list-item-content>
													</v-list-item>
												</v-list>
											</v-menu>
										</div>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
			<template #[`footer.prepend`]>
				<span class="font-weight-bold mx-3">{{ $t('table-legend') }}</span>
				<v-tooltip color="green" top>
					<template #activator="{ on }">
						<div class="d-flex">
							<span class="font-weight-medium font-size12-5">{{ $t('active') }}</span>
							<v-icon class="mdi-18px color-green px-2" v-on="on">mdi-checkbox-blank-circle</v-icon>
						</div>
					</template>
					<span>{{ $t('active') }}</span>
				</v-tooltip>
				<v-tooltip color="grey-darken-2" top>
					<template #activator="{ on }">
						<div class="d-flex">
							<span class="font-weight-medium font-size12-5">{{ $t('inactive') }}</span>
							<v-icon class="mdi-18px px-2" v-on="on">mdi-checkbox-blank-circle</v-icon>
						</div>
					</template>
					<span>{{ $t('inactive') }}</span>
				</v-tooltip>
			</template>
		</v-data-table>
		<ConfirmStatusDialog
			v-if="confirmDialog"
			:dialog="confirmDialog"
			confirmQuestion="confirm-change-status-staff-question"
			@confirmToggleStatus="setStaffStatus"
			@update:dialog="confirmDialog = $event" />
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { AsYouType } from 'libphonenumber-js';
import { getDataTableFooterProps, getRowsForDataTable } from '@/utils/utilities';
import { doctorBlack, nurseBlack } from '@/assets';
import ConfirmStatusDialog from '../../dialogsNew/ConfirmStatusDialog.vue';
export default {
	components: { ConfirmStatusDialog },
	data () {
		return {
			expanded: [],
			loading: true,
			NurseBlack: nurseBlack,
			DoctorBlack: doctorBlack,
			confirmDialog: false,
			staffToToggle: null,
		};
	},
	async mounted () {
		await this.getStaff();
	},
	computed: {
		...mapState({
			relatedUsers: (state) => state.hospitals.users,
			userData: (state) => state.authentication.userData,
			relatedOrganizationMedicalStaff: (state) => state.medicalInstitutions.relatedOrganizationMedicalStaff,
			hospitalData: (state) => state.hospitals.hospitalData,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
			roleName: 'authentication/getRole',
			delay: 'authentication/getDelay',
			appBarSearch: 'filterbar/getSearch',
			appBarRole: 'filterbar/getRole',
			// appBarSpecialization: 'filterbar/getSpecialization',
			medicalStaff: 'hospitals/getMedicalStaff',
			// appBarMedicalStaffStatus: 'filterbar/getMedicalStaffStatus',
			appBarUserStatusVerifier: 'filterbar/getUserStatusVerifier',
		}),

		currentLocale () {
			return this.$i18n.locale === 'en' ? 'spetializationDesc' : 'spetializationDescEsp';
		},
		editDetailsDrawer () {
			return this.$store.state.viewDetailsDrawer;
		},
		headers () {
			let headers = [];
			headers = [
				{ text: this.$t('name'), value: 'fullName', class: 'color-header', width: 200 },
				{ text: this.$t('role'), value: 'role', class: 'color-header' },
				// { text: this.$t('specialization'), class: 'color-header', width: 200 },
				// { text: this.$t('offices'), class: 'color-header', width: 200 },
				{ text: this.$t('email'), value: 'email', class: 'color-header' },
				{ text: this.$t('phone'), value: 'phone', class: 'color-header' },
				{
					text: this.$t('set-status'),
					value: 'status',
					class: 'color-header',
					align: 'center',
					sortable: false,
					width: 50,
				},
				{
					text: this.$t('actions'),
					value: 'status',
					class: 'color-header',
					align: 'center',
					sortable: false,
					width: 50,
				},
				{ text: '', value: '', class: 'color-header', sortable: false },
			];
			headers.pop();
			return headers;
		},
		filteredUsers () {
			if (this.roleName === 'ORGANIZATION_ADMIN') {
				let calculateFileteredStaff =
					this.relatedOrganizationMedicalStaff?.filter((ru) => {
						ru.statusDescription = ru.status === 1 ? 'Active' : 'Inactive';
						ru.fullName = ru.firstName.concat(' ', ru.lastName);
						ru.phone = new AsYouType().input(ru.phone || '');
						ru.specializations = ru?.doctorSpetializations.map((item) => item.spetializationId) ?? [];
						switch (ru.roleId) {
							case 3:
								ru.role = 'Doctor';
								break;
							case 4:
								ru.role = 'Nurse';
								break;
							case 7:
								ru.role = 'Caregiver';
								break;
							default:
								break;
						}
						return ru;
					}) || []; // Default to an empty array if undefined

				if (this.appBarRole !== -99 && this.appBarRole !== null) {
					calculateFileteredStaff = calculateFileteredStaff.filter((u) => u.roleId === this.appBarRole);
				}

				// if (this.appBarSpecialization !== -1 && this.appBarSpecialization !== null) {
				// 	calculateFileteredStaff = calculateFileteredStaff.filter((u) =>
				// 		u.specializations.includes(this.appBarSpecialization),
				// 	);
				// }
				if (
					this.appBarUserStatusVerifier === -1 ||
					this.appBarUserStatusVerifier === 1 ||
					this.appBarUserStatusVerifier === 0
				) {
					calculateFileteredStaff = calculateFileteredStaff.filter((u) => u.status === this.appBarUserStatusVerifier);
				} else if (this.appBarUserStatusVerifier === null && this.appBarUserStatusVerifier === -99) {
					calculateFileteredStaff = calculateFileteredStaff.filter((u) => u.status === this.appBarUserStatusVerifier);
				}

				return calculateFileteredStaff;
			} else if (this.roleName === 'HOSPITAL_ADMIN' || this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
				let calculateFileteredStaff =
					this.medicalStaff?.filter((ru) => {
						if (ru.roleId === 3 || ru.roleId === 4 || ru.roleId === 7) {
							ru.statusDescription = ru.status === 1 ? 'Active' : 'Inactive';
							ru.fullName = ru.firstName.concat(' ', ru.lastName);
							ru.phone = new AsYouType().input(ru?.phone || '');
							ru.specializations =
								ru?.doctorMetadataReadDto?.spetializationData.map((item) => item.spetializationId) ?? [];
							switch (ru.roleId) {
								case 3:
									ru.role = 'Doctor';
									break;
								case 4:
									ru.role = 'Nurse';
									break;
								case 7:
									ru.role = 'Caregiver';
									break;
								default:
									break;
							}
							return ru;
						}
					}) || []; // Default to an empty array if undefined

				// Filter the result
				if (this.appBarRole !== -99 && this.appBarRole !== null) {
					calculateFileteredStaff = calculateFileteredStaff.filter((u) => u.roleId === this.appBarRole);
				}

				// if (this.appBarSpecialization !== -1 && this.appBarSpecialization !== null) {
				// 	calculateFileteredStaff = calculateFileteredStaff.filter((u) =>
				// 		u.specializations.includes(this.appBarSpecialization),
				// 	);
				// }
				if (
					this.appBarUserStatusVerifier === -1 ||
					this.appBarUserStatusVerifier === 1 ||
					this.appBarUserStatusVerifier === 0
				) {
					calculateFileteredStaff = calculateFileteredStaff.filter((u) => u.status === this.appBarUserStatusVerifier);
				} else if (this.appBarUserStatusVerifier === null && this.appBarUserStatusVerifier === -99) {
					calculateFileteredStaff = calculateFileteredStaff.filter((u) => u.status === this.appBarUserStatusVerifier);
				}

				return calculateFileteredStaff;
			} else {
				return [];
			}
		},
		groupedUsers () {
			const groups = this.filteredUsers.reduce((hospitals, user) => {
				const userHospitals = user.hospitalsAssignedData || [];
				userHospitals.forEach((hospital) => {
					let existingHospital = hospitals.find((h) => h.id === hospital.id);
					if (!existingHospital) {
						existingHospital = {
							id: hospital.id,
							hospitalName: hospital.hospitalName,
							users: [],
						};
						hospitals.push(existingHospital);
					}
					existingHospital.users.push(user);
				});
				return hospitals;
			}, []);
			return groups;
		},
		searchUsers () {
			const searchTerm = (this.appBarSearch || '').toLowerCase();
			if (!searchTerm) {
				return this.groupedUsers;
			}
			return this.groupedUsers
				.map((hospital) => {
					if (hospital.hospitalName.toLowerCase().includes(searchTerm)) {
						return hospital;
					}
					const filteredUsers = hospital.users.filter((user) => {
						return user.fullName.toLowerCase().includes(searchTerm) || user.email.toLowerCase().includes(searchTerm);
					});

					if (filteredUsers.length > 0) {
						return {
							...hospital,
							users: filteredUsers,
						};
					}
					return null;
				})
				.filter((hospital) => hospital !== null);
		},
	},
	beforeDestroy () {
		this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		this.$store.dispatch('filterbar/resetSearch');
	},
	methods: {
		getIconForRole (role) {
			switch (role.roleName) {
				case 'NURSE':
				case 'CARE_GIVER':
					return this.NurseBlack;
				case 'DOCTOR':
					return this.DoctorBlack;
			}
		},
		getRowsForDataTable,
		getOddRows (index) {
			if (index % 2 === 1) {
				return 'odd-row';
			}
			if (index % 2 === 0) {
				return 'even-row';
			}
		},
		getStatusColor (item) {
			if (item.status === 1) {
				return { color: '#09CB09' };
			} else {
				return {};
			}
		},
		getDataTableFooterProps,
		handleActionSendEmail (item) {
			this.sendActivationEmail(item);
		},
		getSpecializationDescription (spec) {
			return spec[this.currentLocale];
		},
		async editDrawerDetails (item) {
			this.loading = true;
			if (!this.editDetailsDrawer) {
				const headersData = { UserId: item.id };
				await this.$store.dispatch('users/getUserByIdMinimal', headersData);

				this.$store.commit('SET_EDIT_DETAILS_DRAWER', item);
				this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
			} else {
				this.$store.commit('SET_EDIT_DETAILS_DRAWER', item);
				this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
			}
			this.loading = false;
		},
		async expand (item) {
			if (item === this.expanded[0]) this.expanded = [];
			else this.expanded = [item];
		},
		async getStaff () {
			this.loading = true;
			if (this.roleName === 'ORGANIZATION_ADMIN') {
				const organizationId = this.userData.organizationId;
				await this.$store
					.dispatch('medicalInstitutions/getRelatedOrganizationMedicalStaff', organizationId)
					.then(() => {
						this.loading = false;
					});
			} else if (this.roleName === 'HOSPITAL_ADMIN' || this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
				const hospitalId = this.userData.hospitalAssignedData.id;
				await this.$store.dispatch('hospitals/getRelatedMedicalStaff', hospitalId).then(() => {
					this.loading = false;
				});
			}
		},
		closeDialog () {
			this.getStaff();
		},
		async sendActivationEmail (item, validationType) {
			if (validationType === null || validationType === undefined) {
				validationType = 'RESET_PASSWORD';
			}
			const sendValidationEmail = { email: item.email, validationType: validationType };
			await this.$store.dispatch('users/sendValidationLinkByEmail', sendValidationEmail).then((res) => {
				if (res.resFlag) {
					this.$store.commit('alerts/add', {
						id: 'addedAdmin',
						type: 'success',
						color: 'main_green',
						message: res.msg,
					});
					this.$store.dispatch('alerts/clearWithDelay', this.delay);
				} else {
					this.$store.commit('alerts/add', {
						id: 'errorAddingAdmin',
						type: 'error',
						color: 'main_red',
						message: res.msg,
					});
					this.$store.dispatch('alerts/clearWithDelay', this.delay);
				}
			});
		},
		openConfirmDialog (staff) {
			this.staffToToggle = staff;
			this.confirmDialog = true;
		},
		async setStaffStatus () {
			this.staffToToggle.status = this.staffToToggle.status === 1 ? -1 : 1;
			const body = {
				id: this.staffToToggle.id,
				status: this.staffToToggle.status,
			};

			await this.$store.dispatch('users/setStatus', {
				id: body.id,
				status: body.status,
			});
			this.confirmDialog = false;
		},
	},
};
</script>
<style>
.disabled-row {
	opacity: 0.5;
	cursor: default;
}
.disabled-row:hover {
	background: none !important;
}
</style>
